'use client';

import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import type { SizeType } from '../../models';

import ButtonSpinner from '../Spinners/ButtonSpinner';

export interface TextButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  size: SizeType;
  icon?: {
    position: 'leading' | 'trailing';
    element: ReactNode;
  };
  loading?: boolean;
}

const sharedThemes =
  'flex items-center gap-[8px] font-bold disabled:opacity-40 text-CoolGray-900' +
  ' [&_svg]:text-CoolGray-900 [&_svg]:w-[16px] [&_svg]:h-[16px] focus:ring-0 outline-none hover:text-CoolGray-800 ';

const sizeThemes = {
  xs: 'py-[7px] text-xs leading-[16px] ',
  sm: 'py-[8px] text-sm leading-[18px] ',
  base: 'py-[10px] text-sm leading-[18px] ',
  lg: 'py-[10px] text-base leading-[22px] ',
  xl: 'py-[14px] text-base leading-[22px] ',
};

const TextButton = forwardRef<HTMLButtonElement, TextButtonProps>(
  function Button(
    {
      label,
      size = 'base',
      icon,
      disabled = false,
      loading = false,
      className,
      type = 'button',
      ...props
    },
    ref
  ) {
    const spinner = <ButtonSpinner size={size} colorType="white" />;
    const isTrailingPosition = icon?.position === 'trailing';
    const Icon = loading ? spinner : icon?.element;

    return (
      <button
        {...props}
        ref={ref}
        className={
          sharedThemes +
          sizeThemes[size] +
          (isTrailingPosition ? 'flex-row-reverse ' : '') +
          className
        }
        type={type}
        disabled={disabled || loading}
      >
        {Icon}
        {label}
      </button>
    );
  }
);

export default TextButton;
